import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Shield, Download, Filter, DollarSign, Coins, Wallet, Landmark, Package, ShoppingBag, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useApp } from "@/lib/AppContext";
import { t, HULL_LABELS, STATUS_LABEL } from "@/lib/i18n";
import LogEarningsForm from "@/components/LogEarningsForm";
import PayoutsTable from "@/components/PayoutsTable";
import InviteScoutForm from "@/components/InviteScoutForm";

const fmtUSD = (n) => `$${(n || 0).toLocaleString("en-US")}`;
const STATUSES = ["submitted", "contacted", "signed", "declined", "cancelled"];

const fmtDate = (iso, lang) => {
  try {
    return new Date(iso).toLocaleDateString(lang === "vi" ? "vi-VN" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function OperatorDashboard() {
  const { user, lang } = useApp();
  const [summary, setSummary] = useState(null);
  const [queue, setQueue] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [hullFilter, setHullFilter] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    api.get("/tiers").then((r) => setTiers(r.data.tiers)).catch(() => {});
  }, []);

  const loadEarnings = useCallback(async () => {
    const { data } = await api.get("/operator/earnings");
    setEarnings(data);
  }, []);

  const loadQueue = useCallback(async () => {
    const params = {};
    if (statusFilter) params.status_filter = statusFilter;
    if (hullFilter) params.hull_id = hullFilter;
    const { data } = await api.get("/operator/queue", { params });
    setQueue(data);
  }, [statusFilter, hullFilter]);

  const loadSummary = useCallback(async () => {
    const { data } = await api.get("/operator/summary");
    setSummary(data);
  }, []);

  useEffect(() => {
    loadSummary();
    loadEarnings();
  }, [loadSummary, loadEarnings]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const hulls = summary?.hulls || [];

  const updateStatus = async (id, next) => {
    setBusyId(id);
    try {
      await api.patch(`/operator/referrals/${id}`, { status: next });
      toast.success(t(lang, "op_saved"));
      await Promise.all([loadQueue(), loadSummary()]);
    } catch {
      toast.error(t(lang, "op_save_err"));
    } finally {
      setBusyId(null);
    }
  };

  const onExport = async () => {
    const token = localStorage.getItem("eigoo_token");
    const res = await fetch(`${API}/referrals/export.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `eigoo-partners-operator-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const summaryCards = useMemo(
    () => [
      {
        icon: DollarSign,
        label: t(lang, "op_summary_gross"),
        value: fmtUSD(summary?.gross_setup_fees_usd),
        sub: t(lang, "op_setup_fee_hint"),
        testid: "op-gross-card",
      },
      {
        icon: Coins,
        label: t(lang, "op_summary_scout_payouts"),
        value: fmtUSD(summary?.scout_payouts_usd),
        sub: `$50 × ${summary?.total_signed || 0}`,
        testid: "op-scout-payouts-card",
      },
      {
        icon: Wallet,
        label: t(lang, "op_summary_op_share"),
        value: fmtUSD(summary?.operator_share_usd),
        sub: t(lang, "op_share_hint"),
        highlight: true,
        testid: "op-share-card",
      },
      {
        icon: Landmark,
        label: t(lang, "op_summary_net_fleet"),
        value: fmtUSD(summary?.net_fleet_usd),
        sub: t(lang, "op_net_hint"),
        testid: "op-net-card",
      },
      {
        icon: Package,
        label: t(lang, "op_amazon_card"),
        value: fmtUSD(summary?.amazon_payouts_usd),
        sub: t(lang, "op_amazon_card_sub"),
        testid: "op-amazon-card",
      },
      {
        icon: ShoppingBag,
        label: t(lang, "op_market_card"),
        value: fmtUSD(summary?.marketplace_payouts_usd),
        sub: t(lang, "op_market_card_sub"),
        testid: "op-marketplace-card",
      },
      {
        icon: TrendingUp,
        label: t(lang, "op_partner_payouts_all"),
        value: fmtUSD(summary?.total_partner_payouts_usd),
        sub: t(lang, "op_partner_payouts_all_sub"),
        highlight: true,
        testid: "op-total-partner-card",
      },
    ],
    [summary, lang]
  );

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 space-y-6 lg:space-y-8">
      {/* Hero */}
      <section
        className="relative eigoo-card overflow-hidden p-6 sm:p-8 lg:p-10 anim-fade-up"
        data-testid="operator-hero"
      >
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#d4a843]/10 blur-3xl pointer-events-none" />
        <div className="grid lg:grid-cols-5 gap-6 items-center relative">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#d4a843]/30 bg-[#0f2038] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[#f3c669]">
              <Shield className="w-3 h-3" /> {t(lang, "role_operator")}
            </div>
            <h1 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">
              {t(lang, "op_hero_title")}
            </h1>
            <p className="mt-3 text-slate-300/85 max-w-xl leading-relaxed text-sm sm:text-base">
              {t(lang, "op_hero_sub")}
            </p>
          </div>
          <div className="lg:col-span-2 grid grid-cols-3 gap-3">
            <MiniStat label={t(lang, "total_signed")} value={summary?.total_signed ?? "—"} tone="signed" testid="op-mini-signed" />
            <MiniStat label={t(lang, "total_pending")} value={summary?.total_pending ?? "—"} tone="contacted" testid="op-mini-pending" />
            <MiniStat label={t(lang, "total_referrals")} value={summary?.total_referrals ?? "—"} tone="submitted" testid="op-mini-total" />
          </div>
        </div>
      </section>

      {/* Revenue summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4 lg:gap-6">
        {summaryCards.map((c, i) => (
          <div
            key={c.label}
            data-testid={c.testid}
            className={`eigoo-card p-5 anim-fade-up anim-delay-${(i % 4) + 1}`}
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  {c.label}
                </div>
                <div
                  className={`font-display mt-2 text-2xl sm:text-3xl font-extrabold tabular-nums ${
                    c.highlight ? "gold-gradient-text" : "text-white"
                  }`}
                >
                  {c.value}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">{c.sub}</div>
              </div>
              <div
                className={`w-9 h-9 rounded-xl grid place-items-center border shrink-0 ${
                  c.highlight
                    ? "border-[#d4a843]/50 bg-[#d4a843]/10 text-[#f3c669]"
                    : "border-[#1e3a61] bg-[#0f2038] text-slate-300"
                }`}
              >
                <c.icon className="w-4 h-4" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Log a payout + recent payouts */}
      <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
        <LogEarningsForm
          lang={lang}
          knownSources={summary?.known_sources}
          onLogged={() => {
            loadEarnings();
            loadSummary();
          }}
        />
        <PayoutsTable rows={earnings} lang={lang} showScout />
      </div>

      <InviteScoutForm
        lang={lang}
        tiers={tiers}
        onCreated={() => {
          loadSummary();
        }}
      />

      {/* Breakdowns */}
      <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
        <Breakdown
          title={t(lang, "by_scout")}
          rows={(summary?.by_scout || []).map((s) => ({
            key: s.scout_id,
            label: s.scout_name || s.scout_code || "—",
            sub: s.scout_code ? `#${s.scout_code}` : "",
            metric_a: s.signed,
            metric_b: s.submitted,
            amount: s.payout_usd,
          }))}
          lang={lang}
          testid="op-breakdown-scouts"
        />
        <Breakdown
          title={t(lang, "by_hull")}
          rows={(summary?.by_hull || []).map((h) => ({
            key: h.hull_id,
            label: HULL_LABELS[lang]?.[h.hull_id] || h.hull_name,
            sub: "",
            metric_a: h.signed,
            metric_b: h.submitted,
            amount: h.gross_usd,
          }))}
          lang={lang}
          testid="op-breakdown-hulls"
        />
      </div>

      {/* Delivery queue */}
      <section
        className="eigoo-card p-6 sm:p-7 anim-fade-up anim-delay-3"
        data-testid="op-queue-section"
      >
        <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
          <div>
            <h3 className="font-display text-lg sm:text-xl font-bold">
              {t(lang, "op_queue")}
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">{t(lang, "op_queue_sub")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-slate-400 mr-1">
              <Filter className="w-3.5 h-3.5" /> Filter
            </div>
            <select
              className="eigoo-input !py-2 !px-3 text-sm w-auto"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="op-filter-status"
              aria-label={t(lang, "op_filter_status")}
            >
              <option value="">{t(lang, "op_filter_all")} · {t(lang, "col_status")}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL(lang, s)}
                </option>
              ))}
            </select>
            <select
              className="eigoo-input !py-2 !px-3 text-sm w-auto"
              value={hullFilter}
              onChange={(e) => setHullFilter(e.target.value)}
              data-testid="op-filter-hull"
              aria-label={t(lang, "op_filter_hull")}
            >
              <option value="">{t(lang, "op_filter_all")} · {t(lang, "col_hull")}</option>
              {hulls.map((h) => (
                <option key={h.id} value={h.id}>
                  {HULL_LABELS[lang]?.[h.id] || h.name}
                </option>
              ))}
            </select>
            <button
              onClick={onExport}
              className="ghost-btn inline-flex items-center gap-2 text-sm"
              data-testid="export-csv-button"
            >
              <Download className="w-4 h-4" />
              {t(lang, "export_csv")}
            </button>
          </div>
        </div>

        {queue.length === 0 ? (
          <div className="border border-dashed border-[#1e3a61] rounded-xl py-10 text-center text-slate-400 text-sm">
            {t(lang, "empty_referrals")}
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="op-queue-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-[#1e3a61]">
                  <th className="py-3 pr-4">{t(lang, "col_created")}</th>
                  <th className="py-3 pr-4">{t(lang, "col_prospect")}</th>
                  <th className="py-3 pr-4">{t(lang, "col_scout")}</th>
                  <th className="py-3 pr-4">{t(lang, "col_hull")}</th>
                  <th className="py-3 pr-4">{t(lang, "col_status")}</th>
                  <th className="py-3 pr-4">{t(lang, "op_update_status")}</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-[#1e3a61]/50 hover:bg-[#1a3254]/40 transition-colors"
                    data-testid={`op-queue-row-${r.id}`}
                  >
                    <td className="py-3 pr-4 text-slate-300 whitespace-nowrap">
                      {fmtDate(r.created_at, lang)}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="font-semibold text-white">{r.prospect_name}</div>
                      {r.business_name && (
                        <div className="text-[11px] text-slate-500">{r.business_name}</div>
                      )}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="text-slate-200">{r.scout_name}</div>
                      <div className="text-[11px] font-mono text-[#f3c669]/80">
                        #{r.scout_referral_code}
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-slate-300">
                      {HULL_LABELS[lang]?.[r.hull_id] || r.hull_name}
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`status-pill status-${r.status}`}>
                        {STATUS_LABEL(lang, r.status)}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <select
                        className="eigoo-input !py-1.5 !px-2.5 text-xs w-full max-w-[160px]"
                        value={r.status}
                        disabled={busyId === r.id}
                        onChange={(e) => updateStatus(r.id, e.target.value)}
                        data-testid={`op-status-select-${r.id}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {STATUS_LABEL(lang, s)}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <footer className="text-center text-xs text-slate-500 pt-4 pb-8">
        © {new Date().getFullYear()} {t(lang, "footer")}
      </footer>
    </main>
  );
}

function MiniStat({ label, value, tone, testid }) {
  return (
    <div
      className={`rounded-xl border p-3 bg-[#0a1628]/60 border-[#1e3a61]`}
      data-testid={testid}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        {label}
      </div>
      <div className={`font-display text-2xl font-extrabold mt-1 status-${tone} !bg-transparent !border-0 !p-0 !rounded-none !text-current tabular-nums`}>
        {value}
      </div>
    </div>
  );
}

function Breakdown({ title, rows, lang, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.amount || 0));
  return (
    <section className="eigoo-card p-6 sm:p-7 anim-fade-up anim-delay-2" data-testid={testid}>
      <h3 className="font-display text-lg sm:text-xl font-bold mb-4">{title}</h3>
      {rows.length === 0 && (
        <p className="text-xs text-slate-500 italic">{t(lang, "empty_referrals")}</p>
      )}
      <div className="space-y-3">
        {rows.map((r) => {
          const pct = Math.min(100, Math.round((r.amount / max) * 100));
          return (
            <div
              key={r.key}
              className="rounded-xl border border-[#1e3a61] bg-[#0f2038]/60 px-4 py-3"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{r.label}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {r.metric_a} {t(lang, "hull_signed").toLowerCase()} · {r.metric_b}{" "}
                    {t(lang, "hull_submitted").toLowerCase()}
                    {r.sub ? ` · ${r.sub}` : ""}
                  </div>
                </div>
                <div className="font-mono font-bold text-[#f3c669] tabular-nums">
                  {fmtUSD(r.amount)}
                </div>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-[#1e3a61]/60 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#d4a843] to-[#f3c669]"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
